import { Box, Stack, Typography, styled } from "@mui/material"
import type React from "react"
import { ItemContainer, ItemHeader } from "../Charts/LegendStyles"
import { formatDateOnly } from "../../utils/dates"
import { extraColors } from "../../utils/theme"
import {
  NwssTooltipEventMemberRow,
  NwssTooltipInfoRow,
  nwssEventMemberItemSx,
} from "./NwssTooltipInfoRows"
import type { NwssSignalSample } from "./nwssSampleTypes"
import {
  formatPopulationServed,
  getNwssHeatmapCellDetections,
  nwssCountyLabelForSite,
} from "./useNwssSignalSamples"

const HEATMAP_TOOLTIP_DETECTION_LIMIT = 8

const TooltipBody = styled(Box)(() => ({
  padding: 12,
  maxWidth: 300,
  maxHeight: 360,
  overflowY: "auto",
}))

const TooltipHeader = styled(Typography)(() => ({
  fontWeight: 800,
  color: extraColors.translucent50,
  marginBottom: 8,
  display: "block",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  fontSize: "0.65rem",
}))

const TooltipSubheader = styled(Typography)(() => ({
  fontWeight: 700,
  color: extraColors.white,
  marginBottom: 8,
  display: "block",
  fontSize: "0.75rem",
}))

export const nwssHeatmapTooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: extraColors.dark,
      border: `1px solid ${extraColors.translucent8}`,
      boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
      p: 0,
      maxWidth: 320,
    },
  },
} as const

export interface NwssDetectionHeatmapTooltipProps {
  row: string
  weekKey: string
  weekLabel: string
  groupBy: "site" | "county"
  samples: NwssSignalSample[]
  reportingCounties?: string[] | null
  lookbackMonths?: number
  inEventWindow?: boolean
}

export const NwssDetectionHeatmapTooltip: React.FC<
  NwssDetectionHeatmapTooltipProps
> = ({
  row,
  weekKey,
  weekLabel,
  groupBy,
  samples,
  reportingCounties = null,
  lookbackMonths,
  inEventWindow = false,
}) => {
  const detections = getNwssHeatmapCellDetections(
    samples,
    row,
    weekKey,
    groupBy,
    reportingCounties,
    lookbackMonths
  )
  const visible = detections.slice(0, HEATMAP_TOOLTIP_DETECTION_LIMIT)
  const hiddenCount = detections.length - visible.length

  return (
    <TooltipBody>
      <TooltipHeader>Week of {weekLabel}</TooltipHeader>
      <TooltipSubheader>{row}</TooltipSubheader>
      {inEventWindow && (
        <Typography
          variant="caption"
          sx={{
            color: extraColors.translucent50,
            display: "block",
            mb: 0.75,
          }}
        >
          Within event window
        </Typography>
      )}
      <HeatmapDetectionCount count={detections.length} />
      {visible.length > 0 && (
        <HeatmapDetectionList
          detections={visible}
          hiddenCount={hiddenCount}
          groupBy={groupBy}
          samples={samples}
          reportingCounties={reportingCounties}
        />
      )}
    </TooltipBody>
  )
}

interface HeatmapDetectionCountProps {
  count: number
}

const HeatmapDetectionCount: React.FC<HeatmapDetectionCountProps> = ({
  count,
}) => (
  <Typography
    variant="caption"
    sx={{
      color:
        count > 0 ? extraColors.status.redLight : extraColors.translucent50,
      display: "block",
      mb: 1,
      fontWeight: count > 0 ? 700 : 400,
    }}
  >
    {count === 0
      ? "No detections this week"
      : `${count} detection${count === 1 ? "" : "s"}`}
  </Typography>
)

interface HeatmapDetectionListProps {
  detections: NwssSignalSample[]
  hiddenCount: number
  groupBy: "site" | "county"
  samples: NwssSignalSample[]
  reportingCounties: string[] | null
}

const HeatmapDetectionList: React.FC<HeatmapDetectionListProps> = ({
  detections,
  hiddenCount,
  groupBy,
  samples,
  reportingCounties,
}) => (
  <Stack spacing={1}>
    {detections.map(sample => (
      <HeatmapDetectionCard
        key={`${sample.record_id}-${sample.sample_collect_date}`}
        sample={sample}
        groupBy={groupBy}
        allSamples={samples}
        reportingCounties={reportingCounties}
      />
    ))}
    {hiddenCount > 0 && <HeatmapHiddenCountNote hiddenCount={hiddenCount} />}
  </Stack>
)

interface HeatmapHiddenCountNoteProps {
  hiddenCount: number
}

const HeatmapHiddenCountNote: React.FC<HeatmapHiddenCountNoteProps> = ({
  hiddenCount,
}) => (
  <Typography variant="caption" sx={{ color: extraColors.translucent50 }}>
    +{hiddenCount} more detection{hiddenCount === 1 ? "" : "s"}
  </Typography>
)

interface HeatmapDetectionCardProps {
  sample: NwssSignalSample
  groupBy: "site" | "county"
  allSamples: NwssSignalSample[]
  reportingCounties: string[] | null
}

const HeatmapDetectionCard: React.FC<HeatmapDetectionCardProps> = ({
  sample,
  groupBy,
  allSamples,
  reportingCounties,
}) => (
  <ItemContainer sx={nwssEventMemberItemSx(sample.is_event_member)}>
    <ItemHeader>
      <Typography variant="small1">
        {formatDateOnly(sample.sample_collect_date)}
      </Typography>
    </ItemHeader>
    <HeatmapDetectionDetailRows
      sample={sample}
      showSite={groupBy === "county"}
      showCounty={groupBy === "site"}
      allSamples={allSamples}
      reportingCounties={reportingCounties}
    />
  </ItemContainer>
)

interface HeatmapDetectionDetailRowsProps {
  sample: NwssSignalSample
  showSite: boolean
  showCounty: boolean
  allSamples: NwssSignalSample[]
  reportingCounties: string[] | null
}

const HeatmapDetectionDetailRows: React.FC<HeatmapDetectionDetailRowsProps> = ({
  sample,
  showSite,
  showCounty,
  allSamples,
  reportingCounties,
}) => {
  const county =
    showCounty && sample.site
      ? nwssCountyLabelForSite(allSamples, sample.site, reportingCounties)
      : null

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {showSite && <NwssTooltipInfoRow label="Site" value={sample.site} />}
      {showCounty && county && (
        <NwssTooltipInfoRow label="County" value={county} />
      )}
      {sample.population_served != null && (
        <NwssTooltipInfoRow
          label="Population served"
          value={formatPopulationServed(sample.population_served)}
          rowSx={{ gap: 1.5, pr: 0.5 }}
        />
      )}
      {sample.pcr_target && (
        <NwssTooltipInfoRow label="PCR target" value={sample.pcr_target} />
      )}
      {sample.is_event_member && <NwssTooltipEventMemberRow />}
    </Box>
  )
}
